type Localized<T> = T extends string
	? string
	: T extends number
		? number
		: T extends boolean
			? boolean
			: T extends readonly (infer Item)[]
				? ReadonlyArray<Localized<Item>>
				: T extends object
					? { readonly [Key in keyof T]: Localized<T[Key]> }
					: T;

const reliabilityTextEn = {
	metadata: {
		title: "Reliability & 99.9999% Uptime",
		description:
			"Automatic failover across providers, real-time health monitoring, and intelligent routing. Never go down, even when your providers do.",
		openGraph: {
			title: "Reliability & 99.9999% Uptime - doteb",
			description:
				"Automatic failover across providers, real-time health monitoring, and intelligent routing. Never go down, even when your providers do.",
		},
	},
	hero: {
		eyebrow: "RELIABILITY",
		eyebrowDetail: "99.9999% effective uptime",
		title: "Your AI app can't afford to go down.",
		description:
			"doteb automatically routes requests to healthy providers in real time. When one goes down, your traffic seamlessly fails over - your users never notice.",
		primaryCta: "Start Routing in Minutes",
		secondaryCta: "Talk to Sales",
		stats: {
			uptime: "Effective uptime",
			downtime: "Downtime per year",
			providers: "Providers",
			overhead: "Failover overhead",
		},
	},
	failover: {
		eyebrow: "HOW IT WORKS",
		title: "Automatic failover in milliseconds.",
		description:
			"Every request is health-checked in real time. The moment a provider starts failing, returning 5xx, or timing out, traffic is diverted to the next healthy one - on the same request.",
		cards: [
			{
				title: "Provider fails",
				description:
					"An upstream provider returns a 5xx, times out, or rate limits your request. We detect it within the same request cycle.",
			},
			{
				title: "Instant re-route",
				description:
					"The Gateway automatically retries the same prompt against the next healthy provider for that model, so your app does not experience additional latency.",
			},
			{
				title: "Response delivered",
				description:
					"Your user gets their answer. Our status dashboard records the incident for you - your service stays up even when providers don't.",
			},
		],
		flowStart: "Works with every request",
		flowEnd: "No SDK changes. No config.",
	},
	features: {
		eyebrow: "WHAT'S INCLUDED",
		title: "Built for production traffic.",
		description:
			"Reliability is the default - not an add-on. Every account gets the full routing engine.",
		items: [
			{
				title: "Real-time health checks",
				description:
					"Every provider is continuously probed. Unhealthy endpoints are taken out of rotation within seconds.",
			},
			{
				title: "Smart routing by latency",
				description:
					"Requests go to the fastest responsive provider for your region. TTFT is tracked per provider, per model.",
			},
			{
				title: "Multi-region redundancy",
				description:
					"Route across providers spread across US, EU, and APAC so a regional outage never takes you down.",
			},
			{
				title: "Rate-limit aware",
				description:
					"When a provider throttles you, traffic shifts automatically - you keep serving requests without manual intervention.",
			},
			{
				title: "Observable by default",
				description:
					"Uptime, error rates, and latency tracked per provider in your dashboard. Use it in audits or share with stakeholders.",
			},
			{
				title: "SLA reporting",
				description:
					"Export uptime and performance reports for compliance. Enterprise plans include 99.9% SLAs with credits.",
			},
		],
	},
	cta: {
		title: "Stop babysitting provider dashboards.",
		description:
			"Switch your base URL to doteb and get automatic failover, real-time health monitoring, and uptime reporting across 25+ providers - in one line of code.",
		primaryCta: "Get Started Free",
		secondaryCta: "Talk to Sales",
	},
} as const;

const reliabilityTextZh = {
	metadata: {
		title: "可靠性与 99.9999% Uptime",
		description:
			"跨 providers 自动 failover、实时健康监控和智能路由。即使上游 providers 出问题，你的服务也不应宕机。",
		openGraph: {
			title: "可靠性与 99.9999% Uptime - doteb",
			description:
				"跨 providers 自动 failover、实时健康监控和智能路由。即使上游 providers 出问题，你的服务也不应宕机。",
		},
	},
	hero: {
		eyebrow: "可靠性",
		eyebrowDetail: "99.9999% 有效 uptime",
		title: "你的 AI 应用不能承受宕机。",
		description:
			"doteb 会实时将请求路由到健康的 providers。当某个 provider 不可用时，流量会无感 failover，用户不会察觉。",
		primaryCta: "几分钟内开始路由",
		secondaryCta: "联系销售",
		stats: {
			uptime: "有效 uptime",
			downtime: "每年停机时间",
			providers: "Providers",
			overhead: "Failover 开销",
		},
	},
	failover: {
		eyebrow: "工作方式",
		title: "毫秒级自动 failover。",
		description:
			"每个请求都会实时健康检查。一旦 provider 开始失败、返回 5xx 或超时，流量会在同一个请求内被转移到下一个健康 provider。",
		cards: [
			{
				title: "Provider 失败",
				description:
					"上游 provider 返回 5xx、超时或对请求限流。我们会在同一个请求周期内检测到。",
			},
			{
				title: "即时重路由",
				description:
					"Gateway 会自动用同一个 prompt 重试该模型的下一个健康 provider，因此你的应用不会感受到额外延迟。",
			},
			{
				title: "响应送达",
				description:
					"用户拿到答案。状态仪表盘会为你记录事件，即使 providers 不稳定，你的服务仍保持在线。",
			},
		],
		flowStart: "适用于每个请求",
		flowEnd: "无需改 SDK。无需配置。",
	},
	features: {
		eyebrow: "包含能力",
		title: "为生产流量构建。",
		description: "可靠性是默认能力，不是附加项。每个账号都包含完整路由引擎。",
		items: [
			{
				title: "实时健康检查",
				description: "持续探测每个 provider。不健康端点会在数秒内移出轮转。",
			},
			{
				title: "按延迟智能路由",
				description:
					"请求会发送到你所在区域响应最快的 provider。TTFT 按 provider、按模型跟踪。",
			},
			{
				title: "多区域冗余",
				description:
					"跨 US、EU 和 APAC 的 providers 路由，区域性故障不会拖垮你的服务。",
			},
			{
				title: "感知速率限制",
				description:
					"当 provider 对你限流时，流量会自动迁移，无需人工介入也能继续服务请求。",
			},
			{
				title: "默认可观测",
				description:
					"在 dashboard 中按 provider 跟踪 uptime、错误率和延迟，可用于审计或分享给相关团队。",
			},
			{
				title: "SLA 报告",
				description:
					"导出 uptime 和性能报告用于合规。企业版方案包含带 credits 的 99.9% SLA。",
			},
		],
	},
	cta: {
		title: "不用再盯着 provider dashboard。",
		description:
			"把 base URL 切到 doteb，用一行代码获得跨 25+ providers 的自动 failover、实时健康监控和 uptime 报告。",
		primaryCta: "免费开始",
		secondaryCta: "联系销售",
	},
} satisfies Localized<typeof reliabilityTextEn>;

const reliabilityText = {
	en: reliabilityTextEn,
	"zh-CN": reliabilityTextZh,
};

export function selectReliabilityText(
	locale: string | undefined,
): Localized<typeof reliabilityTextEn> {
	return locale === "zh-CN" ? reliabilityText["zh-CN"] : reliabilityText.en;
}
